/**
 * Catalog picker assist (application / instance) for IGA + Configure SSO wizards.
 * List chip picks stay deterministic; unsure / help / free-text questions → agent.
 */
import type { WizardQuickOption } from './vysorWizardEngine';
import {
  buildAgentContextHeader,
  isUnsureInput,
  shouldDelegateToAgent,
  DYNAMIC_AI_REPLY_RULES,
} from './vysorWizardAssistCommon';

export type CatalogPickerField = 'application_id' | 'instance_id';

export type CatalogPickerAssistOutcome =
  | { kind: 'pass' }
  | { kind: 'agent'; message: string; attachmentText?: string }
  | { kind: 'invalid'; hint: string };

export function buildCatalogPickerAgentContext(
  flowTitle: string,
  fieldKey: CatalogPickerField,
  collected: Record<string, string>,
  userMessage: string,
): string {
  const lines = [
    `User is on ${flowTitle} — picking ${fieldKey === 'application_id' ? 'an application' : 'an environment/instance'}.`,
    collected.application_name ? `App so far: ${collected.application_name}` : '',
    collected.instance_name ? `Environment so far: ${collected.instance_name}` : '',
    '',
    DYNAMIC_AI_REPLY_RULES,
    fieldKey === 'application_id'
      ? 'Help them choose the right catalog app. Prefer chips / exact names — never invent UUIDs.'
      : 'Help them choose the right environment. Prefer chips / exact names — never invent UUIDs.',
    '',
    `User: ${userMessage}`,
  ].filter(Boolean);
  return buildAgentContextHeader(`${flowTitle} — catalog picker`, lines);
}

/**
 * Chip / exact list pick → pass. Unsure / help / conversational free text → agent.
 * Short app names that aren't "help" still pass so the engine can match the catalog.
 */
export function resolveCatalogPickerAssistInput(
  flowTitle: string,
  fieldKey: CatalogPickerField,
  rawAnswer: string,
  optionValue: string | undefined,
  collected: Record<string, string>,
  assistActive: boolean,
): CatalogPickerAssistOutcome {
  const trimmed = rawAnswer.trim();

  // List chip / action values always pass to the engine
  if (
    optionValue &&
    (optionValue.startsWith('app:') ||
      optionValue.startsWith('inst:') ||
      optionValue.startsWith('__') ||
      optionValue === '__cancel__')
  ) {
    return { kind: 'pass' };
  }

  if (isUnsureInput(trimmed) || shouldDelegateToAgent(trimmed, assistActive)) {
    return {
      kind: 'agent',
      message: buildCatalogPickerAgentContext(
        flowTitle,
        fieldKey,
        collected,
        trimmed || `I need help picking the ${fieldKey === 'application_id' ? 'application' : 'environment'}.`,
      ),
    };
  }

  if (!trimmed) {
    return {
      kind: 'invalid',
      hint:
        fieldKey === 'application_id'
          ? 'Pick an app chip, type an exact name, or ask in your own words.'
          : 'Pick an environment chip, type an exact name, or ask in your own words.',
    };
  }

  // Short catalog names / numbers → engine match
  return { kind: 'pass' };
}

export function buildCatalogPickerRestoreOptions(
  existing: WizardQuickOption[] | undefined,
): WizardQuickOption[] {
  if (existing?.length) return existing;
  return [{ id: 'wz_cancel', label: 'Cancel', value: '__cancel__' }];
}
